// Accounts data access (Stage 2A).
// Reads are limited to Admin by row-level security; every change goes through an
// admin-checked database function (migrations/008_acc_ledger_core.sql).
// There are deliberately NO direct table writes in this module.
import { supabase } from "../supabase";
import { optimizedForUpload } from "../lib/uploadCompression";
import { storeFile, fileUrl } from "../lib/fileStore";

function unwrap({ data, error }) {
    if (error) throw new Error(error.message || "Request failed");
    return data;
}
const rpc = async (fn, args = {}) => unwrap(await supabase.rpc(fn, args));
const notInstalled = (err) => !!err && (err.code === "42P01" || err.code === "PGRST205" || /does not exist|Could not find the table/i.test(err.message || ""));

// Everything the Accounts screens show, in one round of parallel reads.
export async function loadAccounts() {
    const settings = await supabase.from("acc_settings").select("*").maybeSingle();
    if (notInstalled(settings.error)) return { installed: false };
    if (settings.error) throw new Error(settings.error.message);
    const q = (t, cols = "*", order) => {
        let r = supabase.from(t).select(cols);
        if (order) r = r.order(order[0], { ascending: order[1] !== false });
        return r;
    };
    const [calendar, years, periods, accounts, mappings, parties, roles, taxCodes, taxRates, staff, tenants, posted] = await Promise.all([
        q("acc_bs_calendar", "*", ["ad_start"]),
        q("acc_fiscal_years", "*", ["start_date"]),
        q("acc_periods", "*", ["start_date"]),
        q("acc_accounts", "*", ["code"]),
        q("acc_account_mappings"),
        q("acc_parties", "*", ["name"]),
        q("acc_party_roles"),
        q("acc_tax_codes", "*", ["code"]),
        q("acc_tax_rates", "*", ["effective_from"]),
        q("staff", "id, name, role, employment_type, employment_status, is_test_account", ["name"]),
        q("tenants", "id, name", ["name"]),
        supabase.from("acc_journal_entries").select("id", { count: "exact", head: true }).eq("status", "posted"),
    ]);
    for (const r of [calendar, years, periods, accounts, mappings, parties, roles, taxCodes, taxRates, staff, tenants, posted]) {
        if (r.error) throw new Error(r.error.message);
    }
    // Activation requirements as the database will enforce them (migration 009). Older databases
    // without 009 return an error here; the screens then fall back to their own checklist.
    const activation = await supabase.rpc("acc_activation_check");
    // Go-live layer (migration 011): switch-on readiness, opening-balance status, auditor sign-offs.
    // Databases without 011 return errors here and the screens hide the go-live parts.
    const [ready, opening, signoffs] = await Promise.all([
        supabase.rpc("acc_readiness_check"), supabase.rpc("acc_opening_status"),
        supabase.from("acc_signoffs").select("*").order("recorded_at", { ascending: false })]);
    const has2C = !ready.error && !opening.error;
    // Provisional activation (migration 021): the owner's approval to switch on before the auditor's chart review. Absent before 021.
    const prov = await supabase.from("acc_provisional_activations").select("*").order("recorded_at", { ascending: false });
    // Historical books (migration 018): first-year declaration, packages, open items. Absent before 018.
    const hist = await loadHistorical();
    const firstYear = !!hist.has2F && hist.approvals.some(a => a.kind === "nil_opening" && !a.revoked_at);
    return {
        hist, has2F: !!hist.has2F, firstYear, has021: !prov.error, provisionalApprovals: prov.error ? [] : prov.data,
        installed: true, activation: activation.error ? null : activation.data, settings: settings.data, calendar: calendar.data, years: years.data, periods: periods.data,
        has2C, readiness: has2C ? ready.data : null, opening: has2C ? opening.data : null, signoffs: has2C && !signoffs.error ? signoffs.data : [],
        accounts: accounts.data, mappings: mappings.data, parties: parties.data, roles: roles.data,
        taxCodes: taxCodes.data, taxRates: taxRates.data, staff: staff.data, tenants: tenants.data, postedCount: posted.count || 0,
    };
}

export const loadActivity = async ({ limit = 300 } = {}) =>
    unwrap(await supabase.from("acc_audit_log").select("id, at, actor_staff_id, action, table_name, record_id, before, after, reason")
        .order("id", { ascending: false }).limit(limit));

export const integrityCheck = () => rpc("acc_integrity_check");
// Paid Government Taxes (025): TDS payable recorded and still free to deposit (posted liability less saved, unposted deposits).
export const tdsPayableAvailable = () => rpc("acc_tds_payable_available");

// Company settings
export const updateSettings = ({ companyName, pan, dateDisplay }) =>
    rpc("acc_update_settings", { p_company_name: companyName, p_pan: pan || null, p_date_display: dateDisplay });
export const setBooksStart = (date, reason) => rpc("acc_set_books_start", { p_date: date, p_reason: reason });
export const setAccountsEnabled = (enabled, reason) => rpc("acc_set_enabled", { p_enabled: enabled, p_reason: reason });
export const setVat = (registered, effectiveFrom, reason) =>
    rpc("acc_set_vat", { p_registered: registered, p_effective_from: effectiveFrom || null, p_reason: reason });

// Calendar, fiscal years, periods
export const setBsMonth = (bsYear, bsMonth, days, adStart, source) =>
    rpc("acc_set_bs_month", { p_bs_year: bsYear, p_bs_month: bsMonth, p_days: days, p_ad_start: adStart, p_source: source });
export const verifyBsMonth = (bsYear, bsMonth, ref) => rpc("acc_verify_bs_month", { p_bs_year: bsYear, p_bs_month: bsMonth, p_ref: ref });
export const createFiscalYear = (bsYear) => rpc("acc_create_fiscal_year", { p_bs_year: bsYear });
export const lockPeriod = (periodId, reason) => rpc("acc_lock_period", { p_period_id: periodId, p_reason: reason });
export const unlockPeriod = (periodId, reason) => rpc("acc_unlock_period", { p_period_id: periodId, p_reason: reason });

// Chart of Accounts
export const createAccount = ({ code, name, type, parentCode, isPostable, isContra, requiresParty, fixedPartyId, cashFlowClass, description }) =>
    rpc("acc_create_account", {
        p_code: code, p_name: name, p_type: type, p_parent_code: parentCode, p_is_postable: isPostable, p_is_contra: isContra,
        p_requires_party: requiresParty, p_fixed_party_id: fixedPartyId || null, p_cash_flow_class: cashFlowClass || null,
        p_description: description || null,
    });
export const updateAccount = (id, { name, active, cashFlowClass, description }) =>
    rpc("acc_update_account", { p_id: id, p_name: name, p_active: active, p_cash_flow_class: cashFlowClass || null, p_description: description || null });

// Parties
export const createParty = ({ name, roles, staffId, tenantId, pan, contact }) =>
    rpc("acc_create_party", { p_name: name, p_roles: roles, p_staff_id: staffId || null, p_tenant_id: tenantId || null, p_pan: pan || null, p_contact: contact || null });
export const updateParty = (id, { name, pan, contact, active, roles }) =>
    rpc("acc_update_party", { p_id: id, p_name: name, p_pan: pan || null, p_contact: contact || null, p_active: active, p_roles: roles });

// Taxes
export const createTaxCode = ({ code, name, kind, direction, accountId }) =>
    rpc("acc_create_tax_code", { p_code: code, p_name: name, p_kind: kind, p_direction: direction, p_account_id: accountId });
export const addTaxRate = ({ taxCodeId, rate, from, to, sourceRef }) =>
    rpc("acc_add_tax_rate", { p_tax_code_id: taxCodeId, p_rate: rate, p_from: from, p_to: to || null, p_source_ref: sourceRef || null });
export const verifyTaxRate = (rateId, sourceRef) => rpc("acc_verify_tax_rate", { p_rate_id: rateId, p_source_ref: sourceRef });
export const setTaxCodeActive = (id, active, reason) => rpc("acc_set_tax_code_active", { p_id: id, p_active: active, p_reason: reason });

// ─── Stage 2B: transactions & journal workflow (migration 010) ─────────────
// Lines for Money In / Money Out / Move money / bills / payroll are built by the database
// from the chosen purpose; this module never sends journal lines for them.
export async function loadWorkspaceExtras() {
    const [money, purposes] = await Promise.all([
        supabase.from("acc_money_accounts").select("*").order("display_name"),
        supabase.from("acc_txn_purposes").select("*").eq("active", true).order("sort"),
    ]);
    if (money.error || purposes.error) return { has2B: false, moneyAccounts: [], purposes: [] };
    return { has2B: true, moneyAccounts: money.data, purposes: purposes.data };
}
export const loadTransactions = async ({ from, to }) =>
    unwrap(await supabase.from("acc_v_transactions").select("*").gte("txn_date", from).lte("txn_date", to)
        .order("txn_date", { ascending: false }).order("created_at", { ascending: false }).limit(1000));
export const loadBills = async () =>
    unwrap(await supabase.from("acc_v_transactions").select("*").eq("kind", "bill").order("txn_date", { ascending: false }).limit(1000));
export const loadBillPayments = async (billId) =>
    unwrap(await supabase.from("acc_v_transactions").select("*").eq("bill_id", billId).order("txn_date"));
export async function loadReadyQueue() {
    const [txns, manual] = await Promise.all([
        supabase.from("acc_v_transactions").select("*").eq("entry_status", "ready").order("txn_date"),
        supabase.from("acc_v_journal").select("*").eq("status", "ready").eq("entry_type", "manual").order("entry_date"),
    ]);
    if (txns.error) throw new Error(txns.error.message);
    if (manual.error) throw new Error(manual.error.message);
    return { txns: txns.data, manual: manual.data };
}
export const loadJournal = async ({ from, to }) =>
    unwrap(await supabase.from("acc_v_journal").select("*").gte("entry_date", from).lte("entry_date", to)
        .order("entry_date", { ascending: false }).order("prepared_at", { ascending: false }).limit(1000));
export async function loadEntryDetail(entryId) {
    const [entry, lines, txn, files, history] = await Promise.all([
        supabase.from("acc_v_journal").select("*").eq("id", entryId).maybeSingle(),
        supabase.from("acc_journal_lines").select("*, account:acc_accounts(code, name, type), party:acc_parties(name)").eq("entry_id", entryId).order("line_no"),
        supabase.from("acc_v_transactions").select("*").eq("journal_entry_id", entryId).maybeSingle(),
        supabase.from("acc_attachments").select("*").eq("owner_type", "journal_entry").eq("owner_id", entryId).order("uploaded_at"),
        supabase.from("acc_audit_log").select("id, at, actor_staff_id, action, table_name, record_id, reason, before, after")
            .or(`record_id.eq.${entryId},record_id.like.${entryId}/*`).order("id", { ascending: false }).limit(200),
    ]);
    for (const r of [entry, lines, txn, files, history]) if (r.error) throw new Error(r.error.message);
    let txnHistory = [];
    if (txn.data) {
        const h = await supabase.from("acc_audit_log").select("id, at, actor_staff_id, action, table_name, record_id, reason, before, after")
            .eq("record_id", txn.data.id).order("id", { ascending: false }).limit(100);
        if (h.error) throw new Error(h.error.message);
        txnHistory = h.data;
    }
    return { entry: entry.data, lines: lines.data, txn: txn.data, files: files.data,
        history: [...history.data, ...txnHistory].sort((a, b) => b.id - a.id) };
}
export const previewTransaction = (p) => rpc("acc_txn_preview", { p });
export const saveTransaction = (p) => rpc("acc_save_transaction", { p });
export const setTransactionStatus = (id, status, expectedUpdatedAt) =>
    rpc("acc_set_transaction_status", { p_id: id, p_status: status, p_expected_updated_at: expectedUpdatedAt });
export const entryChecksum = (entryId) => rpc("acc_entry_checksum", { p_id: entryId });
export const postTransaction = async (id, entryId) => rpc("acc_post_transaction", { p_id: id, p_expected_checksum: await entryChecksum(entryId) });
export const reverseTransaction = (id, date, reason) => rpc("acc_reverse_transaction", { p_id: id, p_date: date, p_reason: reason });
export const correctTransaction = (id, date, reason) =>
    rpc("acc_correct_transaction", { p_id: id, p_date: date, p_reason: reason, p_client_request_id: crypto.randomUUID() });
export const deleteTransactionDraft = (id, reason) => rpc("acc_delete_transaction_draft", { p_id: id, p_reason: reason });
export const saveMoneyAccount = (p) => rpc("acc_save_money_account", { p });
export const moneyBalances = (asOf) => rpc("acc_money_account_balances", { p_as_of: asOf });
export const accountLedger = (accountId, partyId, from, to) => rpc("acc_account_ledger", { p_account: accountId, p_party: partyId || null, p_from: from, p_to: to });
export const trialBalance = (from, to) => rpc("acc_trial_balance", { p_from: from, p_to: to });
export const moneySummary = (from, to) => rpc("acc_money_summary", { p_from: from, p_to: to });
// With 011: the same figures, leaving the brought-forward opening package out of Money In/Out.
export const periodSummary = (from, to) => rpc("acc_period_summary", { p_from: from, p_to: to });
// Advanced journal entry (2A functions): the accountant enters the lines.
export const saveJournalDraft = (id, date, narration, reference, lines) =>
    rpc("acc_save_draft", { p_id: id || null, p_entry_date: date, p_narration: narration, p_reference: reference || null, p_lines: lines, p_entry_type: "manual", p_series: "JV" });
export const setEntryStatus = (id, status) => rpc("acc_set_entry_status", { p_id: id, p_status: status });
export const postEntry = async (entryId) => rpc("acc_post_entry", { p_id: entryId, p_expected_checksum: await entryChecksum(entryId) });
export const reverseEntry = (entryId, date, reason) => rpc("acc_reverse_entry", { p_id: entryId, p_date: date, p_reason: reason });
export const deleteJournalDraft = (entryId, reason) => rpc("acc_delete_draft", { p_id: entryId, p_reason: reason });
// Evidence: private bucket, Admin-only; files are never deleted, only hidden with a reason.
export const DOCS_BUCKET = "accounting-docs";
// Stored in R2 when switched on (folder chosen by the database from the entry), otherwise in the bucket as before.
async function storeAttachment(ownerType, ownerId, chosen) {
    const file = await optimizedForUpload(chosen);   // proofs/evidence: optimised image (or the original) — the stored hash is of this file
    const ext = (file.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "");
    const mime = file.type || "application/octet-stream";
    const { ref, sha256 } = await storeFile({ target: `acc.${ownerType}`, ownerId, blob: file, fileName: file.name, mime,
        legacy: { bucket: DOCS_BUCKET, path: `${ownerType}/${ownerId}/${crypto.randomUUID()}.${ext}`, contentType: file.type || undefined } });
    const id = await rpc("acc_register_attachment", { p_owner_type: ownerType, p_owner_id: ownerId, p_storage_path: ref, p_file_name: file.name,
        p_mime_type: mime, p_size_bytes: file.size, p_sha256: sha256 });
    return { id, sha256 };
}
export async function attachFile(entryId, chosen) {
    return (await storeAttachment("journal_entry", entryId, chosen)).id;
}
export const openFile = (path) => fileUrl(DOCS_BUCKET, path);
export const hideFile = (id, reason) => rpc("acc_hide_attachment", { p_id: id, p_reason: reason });
// Stage 2D-1 (D5): which of these journal entries have at least one visible evidence file (for the Review warning).
export async function entriesWithEvidence(entryIds) {
    if (!entryIds.length) return new Set();
    const rows = unwrap(await supabase.from("acc_attachments").select("owner_id").eq("owner_type", "journal_entry").in("owner_id", entryIds).is("hidden_at", null));
    return new Set(rows.map(r => r.owner_id));
}
// Stage 2D-2: freelancer bills (for "Pay a freelancer bill") and the party statement (Admin; posted entries only).
export const loadFreelancerBills = async () =>
    unwrap(await supabase.from("acc_v_transactions").select("*").eq("kind", "freelancer_bill").order("txn_date", { ascending: false }).limit(1000));
export const partyStatement = (partyId, from, to) => rpc("acc_party_statement", { p_party: partyId, p_from: from, p_to: to });
export const loadTransaction = async (id) => unwrap(await supabase.from("acc_v_transactions").select("*").eq("id", id).single());

// ─── Go-live: opening package and auditor sign-offs (migration 011) ─────────
const sha256Hex = async (buf) => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", buf))).map(b => b.toString(16).padStart(2, "0")).join("");
export { sha256Hex };
export const openingStatus = () => rpc("acc_opening_status");
export const readinessCheck = () => rpc("acc_readiness_check");
export const saveOpeningPackage = (p) => rpc("acc_save_opening_package", { p });
export const openingChecksum = (packageId) => rpc("acc_opening_checksum", { p_package: packageId });
export const postOpening = (packageId, checksum, confirmation) =>
    rpc("acc_post_opening", { p_package: packageId, p_expected_checksum: checksum, p_confirmation: confirmation });
export const reverseOpening = (packageId, reason) => rpc("acc_reverse_opening", { p_package: packageId, p_reason: reason });
export const recordSignoff = (p) => rpc("acc_record_signoff", { p });
export const revokeSignoff = (id, reason) => rpc("acc_revoke_signoff", { p_id: id, p_reason: reason });
// Provisional activation (021): Admin only; never an auditor's sign-off.
export const recordProvisionalActivation = (p) => rpc("acc_record_provisional_activation", { p });
export const revokeProvisionalActivation = (id, reason) => rpc("acc_revoke_provisional_activation", { p_id: id, p_reason: reason });
export const loadOpeningPackage = async (packageId) => {
    const [pkg, items] = await Promise.all([
        supabase.from("acc_opening_packages").select("*").eq("id", packageId).single(),
        supabase.from("acc_opening_items").select("*").eq("package_id", packageId).order("line_no")]);
    if (pkg.error) throw new Error(pkg.error.message);
    if (items.error) throw new Error(items.error.message);
    return { pkg: pkg.data, items: items.data };
};
export const loadOpeningHistory = async (fiscalYearId) =>
    unwrap(await supabase.from("acc_opening_packages").select("*").eq("fiscal_year_id", fiscalYearId).order("prepared_at", { ascending: false }));
export const loadOpeningItemsByTransaction = async (txnIds) => txnIds.length
    ? unwrap(await supabase.from("acc_opening_items").select("transaction_id, original_date, original_reference").in("transaction_id", txnIds)) : [];
// Upload a file privately and register it against an owner (journal entry or fiscal year). Returns { id, sha256 }.
export const attachTo = (ownerType, ownerId, chosen) => storeAttachment(ownerType, ownerId, chosen);
export const loadAttachments = async (ownerType, ownerId) =>
    unwrap(await supabase.from("acc_attachments").select("*").eq("owner_type", ownerType).eq("owner_id", ownerId).order("uploaded_at"));

// ─── Historical books and fiscal-year reports (migration 018, Stage 2F) ─────
// Databases without 018 return errors here; the screens then hide the historical parts.
export async function loadHistorical() {
    const [sources, packages, approvals, openItems] = await Promise.all([
        supabase.from("acc_hist_sources").select("id, code, file_name, file_sha256, row_count, line_ending, trailing_newline, loaded_at").order("loaded_at"),
        supabase.from("acc_hist_packages").select("*").order("created_at", { ascending: false }),
        supabase.from("acc_mgmt_approvals").select("*").order("recorded_at", { ascending: false }),
        supabase.from("acc_hist_open_items").select("*").order("ref")]);
    if (sources.error) return { has2F: false };
    for (const r of [packages, approvals, openItems]) if (r.error) throw new Error(r.error.message);
    return { has2F: true, sources: sources.data, packages: packages.data, approvals: approvals.data, openItems: openItems.data };
}
export const fyStatus = async (fyId) => (await rpc("acc_fy_status", { p_fy: fyId }))?.[0] || null;
export const fyTrialBalance = (fyId) => rpc("acc_fy_trial_balance", { p_fy: fyId, p_include_closing: false });
export const profitLoss = (fyId) => rpc("acc_profit_loss", { p_fy: fyId });
export const balanceSheet = (fyId) => rpc("acc_balance_sheet", { p_fy: fyId });
// Flexible statements (S1, migration 022): any period / as at any date.
export const plPeriod = (from, to) => rpc("acc_pl_period", { p_from: from, p_to: to });
export const bsAsof = (date) => rpc("acc_bs_asof", { p_date: date });
export const cashFlow = (from, to) => rpc("acc_cash_flow", { p_from: from, p_to: to });
export const fyLedger = (accountId, fyId) => rpc("acc_fy_ledger", { p_account: accountId, p_fy: fyId });
export const histChecks = (packageId) => rpc("acc_hist_package_checks", { p_package: packageId });
export const histTotals = (packageId) => rpc("acc_hist_package_totals", { p_package: packageId });
export const histTrace = (packageId) => rpc("acc_hist_trace", { p_package: packageId });
export const histChecksum = (packageId) => rpc("acc_hist_package_checksum", { p_package: packageId });
// Load an offline package file (deploy/hist-package.mjs output): source evidence, decisions and open items. The database
// re-parses the source lines and proves they rebuild the evidence file's SHA-256; nothing is posted here.
export async function loadPackageFile(pkg) {
    if (pkg?.format !== "acc_hist_package_v1") throw new Error("This is not a historical package file (acc_hist_package_v1).");
    await rpc("acc_hist_load_source", { p: pkg.source });
    for (const o of pkg.open_items || []) await rpc("acc_hist_save_open_item", { p: o });
    return rpc("acc_hist_save_package", { p: pkg.package });
}
export const approvePackage = (packageId, p) => rpc("acc_hist_approve_package", { p_package: packageId, p });
export const postPackage = (packageId, checksum) => rpc("acc_hist_post_package", { p_package: packageId, p_checksum: checksum });
export const reversePackage = (packageId, reason) => rpc("acc_hist_reverse_package", { p_package: packageId, p_reason: reason });
export const revokeApproval = (id, reason) => rpc("acc_hist_revoke_approval", { p_id: id, p_reason: reason });
export const declareNilOpening = (p) => rpc("acc_hist_declare_nil_opening", { p });
export const saveOpenItem = (p) => rpc("acc_hist_save_open_item", { p });
export const resolveOpenItem = (id, p) => rpc("acc_hist_resolve_open_item", { p_id: id, p });
